import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AudioApiKeySection from './myplan/AudioApiKeySection';
import { useAdmin } from '../contexts/AdminContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  BookOpen,
  Copy,
  Check,
  Eye,
  EyeOff,
  Play,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ArrowLeft,
  Key,
  Zap,
  Download,
  Activity,
  Mic,
  AlertTriangle,
  Loader2,
} from 'lucide-react';

// Navegação do sidebar (scroll-spy) — grupos no padrão de docs de mercado.
// Os endpoints entram dinamicamente (método + path) a partir de ENDPOINTS.
const NAV_SECTIONS = [
  {
    label: 'Começar',
    items: [
      { id: 'inicio', label: 'Visão geral' },
      { id: 'quick-start', label: 'Quickstart' },
      { id: 'minha-chave', label: 'Minha chave' },
      { id: 'autenticacao', label: 'Autenticação' },
      { id: 'fluxo', label: 'Fluxo completo' },
    ],
  },
  // grupo "Endpoints" é montado no render (precisa de ENDPOINTS, definido abaixo)
  {
    label: 'Recursos',
    items: [
      { id: 'playground', label: 'Testar API' },
      { id: 'limites', label: 'Limites' },
      { id: 'erros', label: 'Códigos de erro' },
    ],
  },
];

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const BASE_URL = 'https://app.darkplanner.com.br';

// "Teste agora" do Quickstart (texto copiável; o bloco visual replica com highlight)
const QUICKSTART_CURL = `# 1. Envie uma geração
curl -X POST "${BASE_URL}/api/v1/audio/generate" \\
  -H "X-API-Key: dpk_sua_chave_aqui" \\
  -H "Content-Type: application/json" \\
  -d '{"text": "Olá! Este é um teste da API do DarkPlanner.", "voice_id": "SEU_VOICE_ID"}'

# 2. Acompanhe até concluir (troque JOB_ID pelo job_id do passo 1)
curl "${BASE_URL}/api/v1/audio/status/JOB_ID" \\
  -H "X-API-Key: dpk_sua_chave_aqui"

# 3. Pegue as URLs de download (MP3 + 3 legendas SRT)
curl "${BASE_URL}/api/v1/audio/download/JOB_ID" \\
  -H "X-API-Key: dpk_sua_chave_aqui"`;

// ═══════════════════════════════════════════════════════════════
// SYNTAX HIGHLIGHTING HELPERS
// ═══════════════════════════════════════════════════════════════

const SyntaxSpan = ({ color, children }) => (
  <span style={{ color }}>{children}</span>
);

const K = ({ children }) => <SyntaxSpan color="#C792EA">{children}</SyntaxSpan>; // keyword purple
const S = ({ children }) => <SyntaxSpan color="#C3E88D">{children}</SyntaxSpan>; // string green
const N = ({ children }) => <SyntaxSpan color="#F78C6C">{children}</SyntaxSpan>; // number orange
const C = ({ children }) => <SyntaxSpan color="#546E7A">{children}</SyntaxSpan>; // comment gray
const F = ({ children }) => <SyntaxSpan color="#82AAFF">{children}</SyntaxSpan>; // function blue
const P = ({ children }) => <SyntaxSpan color="#FFCB6B">{children}</SyntaxSpan>; // param yellow

const codeBlockStyle = {
  background: '#0a0a0a',
  border: '1px solid #2D3748',
  borderRadius: '8px',
  padding: '16px',
  fontSize: '13px',
  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  lineHeight: 1.7,
  overflowX: 'auto',
  whiteSpace: 'pre',
  color: '#E0E0E0',
};

// ═══════════════════════════════════════════════════════════════
// COPY BUTTON
// ═══════════════════════════════════════════════════════════════

function CopyButton({ text, style: extraStyle }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Falha ao copiar'); }
  };
  return (
    <button onClick={handleCopy} style={{
      position: 'absolute', top: '8px', right: '8px',
      background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '6px', padding: '4px 8px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: '4px',
      fontSize: '11px', color: 'var(--dp-text-tertiary)',
      transition: 'all 0.2s ease', ...extraStyle,
    }}>
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copiado' : 'Copiar'}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════
// CODE TABS (cURL / Python / JavaScript)
// ═══════════════════════════════════════════════════════════════

function CodeTabs({ tabs }) {
  const [active, setActive] = useState(0);
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #2D3748' }}>
        {tabs.map((t, i) => (
          <button key={t.label} onClick={() => setActive(i)} style={{
            padding: '8px 16px', fontSize: '12px', fontWeight: 600,
            fontFamily: 'var(--dp-font)', cursor: 'pointer',
            background: active === i ? '#0a0a0a' : 'transparent',
            color: active === i ? 'var(--dp-blue-500)' : 'var(--dp-text-tertiary)',
            border: 'none', borderBottom: active === i ? '2px solid var(--dp-blue-500)' : '2px solid transparent',
            transition: 'all 0.2s ease',
          }}>
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ position: 'relative' }}>
        <CopyButton text={tabs[active].raw} />
        <pre style={codeBlockStyle}>
          {tabs[active].code || tabs[active].raw}
        </pre>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// METHOD BADGE
// ═══════════════════════════════════════════════════════════════

function MethodBadge({ method }) {
  const isPost = method === 'POST';
  return (
    <span style={{
      fontSize: '11px', fontWeight: 700,
      padding: '3px 10px', borderRadius: '6px',
      fontFamily: 'monospace', letterSpacing: '0.5px',
      color: isPost ? '#60A5FA' : '#34D399',
      background: isPost ? 'rgba(96, 165, 250, 0.12)' : 'rgba(52, 211, 153, 0.12)',
      border: `1px solid ${isPost ? 'rgba(96, 165, 250, 0.25)' : 'rgba(52, 211, 153, 0.25)'}`,
    }}>
      {method}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// ENDPOINT CARD (expandable)
// ═══════════════════════════════════════════════════════════════

// Slug estável p/ âncora/menu de cada endpoint (ex.: /api/v1/audio/voices → ep-voices)
const endpointId = (method, path) =>
  `ep-${method.toLowerCase()}-${path.replace(/^\/api\/v1\/audio\/?/, '').replace(/[^a-z0-9]+/gi, '-') || 'root'}`;

function EndpointCard({ method, path, description, params, codeTabs, responseExample, extra, openSignal }) {
  const [open, setOpen] = useState(false);
  // O menu lateral abre o card ao navegar até ele (catálogo).
  useEffect(() => { if (openSignal) setOpen(true); }, [openSignal]);
  return (
    <div id={endpointId(method, path)} className="dp-top-accent" style={{
      background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
      borderRadius: '12px', overflow: 'hidden', marginBottom: '12px',
      scrollMarginTop: '24px',
      transition: 'border-color 0.2s ease',
    }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', padding: '16px 20px',
        display: 'flex', alignItems: 'center', gap: '12px',
        background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
      }}>
        <MethodBadge method={method} />
        <code style={{
          flex: 1, fontSize: '13px', color: 'var(--dp-text-primary)',
          fontFamily: 'monospace',
        }}>
          {path}
        </code>
        <span style={{ fontSize: '12px', color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', marginRight: '8px' }}>
          {description}
        </span>
        {open ? <ChevronUp size={16} style={{ color: 'var(--dp-text-tertiary)' }} /> : <ChevronDown size={16} style={{ color: 'var(--dp-text-tertiary)' }} />}
      </button>

      {open && (
        <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--dp-border-subtle)' }}>
          {params && params.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', marginBottom: '8px' }}>
                Parâmetros
              </h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', fontFamily: 'var(--dp-font)' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--dp-border-subtle)' }}>
                      {['Nome', 'Tipo', 'Obrigatório', 'Descrição'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--dp-text-tertiary)', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {params.map(p => (
                      <tr key={p.name} style={{ borderBottom: '1px solid var(--dp-border-subtle)' }}>
                        <td style={{ padding: '8px 12px' }}>
                          <code style={{ color: '#FFCB6B', fontSize: '12px' }}>{p.name}</code>
                        </td>
                        <td style={{ padding: '8px 12px', color: '#C792EA' }}>{p.type}</td>
                        <td style={{ padding: '8px 12px', color: p.required ? '#34D399' : 'var(--dp-text-tertiary)' }}>
                          {p.required ? 'Sim' : 'Não'}
                        </td>
                        <td style={{ padding: '8px 12px', color: 'var(--dp-text-secondary)' }}>{p.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {extra}

          {codeTabs && <CodeTabs tabs={codeTabs} />}

          {responseExample && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', marginBottom: '8px' }}>
                Resposta
              </h4>
              <div style={{ position: 'relative' }}>
                <CopyButton text={responseExample.raw} />
                <pre style={codeBlockStyle}>{responseExample.code}</pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SECTION TITLE
// ═══════════════════════════════════════════════════════════════

function SectionTitle({ icon, title, id }) {
  return (
    <div id={id} style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      marginBottom: '16px', marginTop: '44px',
      scrollMarginTop: '80px', // compensa a topbar fixa ao rolar via âncora
    }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '8px',
        background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <h2 style={{
        fontSize: '18px', fontWeight: 700, color: 'var(--dp-text-primary)',
        fontFamily: 'var(--dp-font)', margin: 0,
      }}>
        {title}
      </h2>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ENDPOINT DATA
// ═══════════════════════════════════════════════════════════════

const ENDPOINTS = [
  {
    method: 'GET', path: '/api/v1/audio/voices',
    description: 'Listar vozes',
    params: [],
    codeTabs: [
      {
        label: 'cURL',
        raw: `curl -X GET ${BASE_URL}/api/v1/audio/voices \\\n  -H "X-API-Key: dpk_sua_chave_aqui"`,
        code: <><S>{`curl -X GET ${BASE_URL}/api/v1/audio/voices`}</S>{` \\\n  `}<K>-H</K> <S>"X-API-Key: dpk_sua_chave_aqui"</S></>,
      },
      {
        label: 'Python',
        raw: `import requests\n\nresponse = requests.get(\n    "${BASE_URL}/api/v1/audio/voices",\n    headers={"X-API-Key": "dpk_sua_chave_aqui"}\n)\nvoices = response.json()["voices"]\nfor v in voices:\n    print(f"{v['id']} - {v['name']}")`,
        code: <><K>import</K> requests{'\n\n'}response = requests.<F>get</F>({'\n'}    <S>"{BASE_URL}/api/v1/audio/voices"</S>,{'\n'}    headers={'{'}<S>"X-API-Key"</S>: <S>"dpk_sua_chave_aqui"</S>{'}'}{'\n'}){'\n'}voices = response.<F>json</F>()[<S>"voices"</S>]{'\n'}<K>for</K> v <K>in</K> voices:{'\n'}    <F>print</F>(<S>f"{'{'}</S><P>v['id']</P><S>{'}'} - {'{'}</S><P>v['name']</P><S>{'}'}"</S>)</>,
      },
      {
        label: 'JavaScript',
        raw: `const response = await fetch("${BASE_URL}/api/v1/audio/voices", {\n  headers: { "X-API-Key": "dpk_sua_chave_aqui" }\n});\nconst data = await response.json();\nconsole.log(data.voices);`,
      },
    ],
    responseExample: {
      raw: '{\n  "success": true,\n  "voices": [\n    { "id": "abc123", "name": "Helena", "gender": "female", "preview_url": "https://..." }\n  ],\n  "total": 25\n}',
      code: <>{'{\n  '}<S>"success"</S>: <K>true</K>,{'\n  '}<S>"voices"</S>: [{'\n    '}{'{ '}<S>"id"</S>: <S>"abc123"</S>, <S>"name"</S>: <S>"Helena"</S>, <S>"gender"</S>: <S>"female"</S>, <S>"preview_url"</S>: <S>"https://..."</S>{' }'}{'\n  '}],{'\n  '}<S>"total"</S>: <N>25</N>{'\n}'}</>,
    },
  },
  {
    method: 'POST', path: '/api/v1/audio/generate',
    description: 'Gerar áudio',
    params: [
      { name: 'text', type: 'string', required: true, desc: 'Texto para narração (10-500.000 chars). Aceita pausas SSML: <break time="1.5s"/> (ver seção Pausas)' },
      { name: 'voice_id', type: 'string', required: true, desc: 'ID da voz (obtenha via /voices)' },
      { name: 'title', type: 'string', required: false, desc: 'Nome do arquivo' },
      { name: 'speed', type: 'float', required: false, desc: 'Velocidade 0.5 a 1.5 (default: 1.0)' },
      { name: 'volume', type: 'float', required: false, desc: 'Volume 0.5 a 2.0 (default: 1.0)' },
      { name: 'subtitle_words', type: 'int', required: false, desc: 'Máx. de palavras por linha em legenda.srt, 1-15 (linhas podem ser menores, quebra em pausas naturais)' },
      { name: 'subtitle_case', type: 'string', required: false, desc: 'Formato: normal, upper, lower' },
      { name: 'subtitle_veo_words', type: 'int', required: false, desc: 'Total fixo de palavras por linha em srtsyncpalavra.srt, 1-15 (prompts consistentes)' },
      { name: 'subtitle_veo_min', type: 'int', required: false, desc: 'Duração mínima (s) dos blocos em srtsynctempo.srt, 1-60' },
      { name: 'subtitle_veo_max', type: 'int', required: false, desc: 'Duração máxima (s) dos blocos em srtsynctempo.srt, 1-60 (>= min)' },
      { name: 'is_multivoice', type: 'bool', required: false, desc: 'Modo multivoz: voice_id deve ser o UUID da voz Multivoz (use /multivoice/info), text deve conter slugs entre colchetes. Cobra 1 crédito por narrador único.' },
    ],
    // Pausas SSML — vivem AQUI porque são usadas no texto enviado a ESTE endpoint.
    extra: (
      <div style={{
        marginTop: '16px', padding: '14px 16px', borderRadius: '10px',
        background: 'rgba(13,174,255,0.05)', border: '1px solid rgba(13,174,255,0.15)',
      }}>
        <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', margin: '0 0 8px' }}>
          Pausas no texto (SSML)
        </h4>
        <p style={{ fontSize: '12.5px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', margin: '0 0 10px', lineHeight: 1.7 }}>
          Insira pausas em qualquer ponto do <code style={{ color: '#FFCB6B', fontSize: '11.5px' }}>text</code> com{' '}
          <code style={{ color: '#FFCB6B', background: '#0a0a0a', padding: '2px 6px', borderRadius: '4px', fontSize: '11.5px' }}>{'<break time="Xs"/>'}</code>.
          A limpeza preserva a tag e o TTS respeita a duração. Aceita decimais (0.5s, 1.5s, 3s).
        </p>
        <div style={{ position: 'relative' }}>
          <CopyButton text={'Bem-vindo ao canal. <break time="1.5s"/> Hoje vamos falar de um mistério <break time="0.5s"/> que ninguém resolveu. <break time="3s"/> Vamos começar.'} />
          <pre style={{ ...codeBlockStyle, whiteSpace: 'pre-wrap' }}>
            Bem-vindo ao canal. <K>{'<break time="1.5s"/>'}</K> Hoje vamos falar de um mistério <K>{'<break time="0.5s"/>'}</K> que ninguém resolveu. <K>{'<break time="3s"/>'}</K> Vamos começar.
          </pre>
        </div>
      </div>
    ),
    codeTabs: [
      {
        label: 'cURL',
        raw: `curl -X POST ${BASE_URL}/api/v1/audio/generate \\\n  -H "X-API-Key: dpk_sua_chave_aqui" \\\n  -H "Content-Type: application/json" \\\n  -d '{"text": "Olá, este é um teste.", "voice_id": "abc123", "speed": 1.0}'`,
        code: <><S>{`curl -X POST ${BASE_URL}/api/v1/audio/generate`}</S>{` \\\n  `}<K>-H</K> <S>"X-API-Key: dpk_sua_chave_aqui"</S>{` \\\n  `}<K>-H</K> <S>"Content-Type: application/json"</S>{` \\\n  `}<K>-d</K> <S>{'\'{"text": "Olá, este é um teste.", "voice_id": "abc123", "speed": 1.0}\''}</S></>,
      },
      {
        label: 'Python',
        raw: `import requests\n\nresponse = requests.post(\n    "${BASE_URL}/api/v1/audio/generate",\n    headers={"X-API-Key": "dpk_sua_chave_aqui", "Content-Type": "application/json"},\n    json={"text": "Olá, este é um teste.", "voice_id": "abc123", "speed": 1.0}\n)\njob_id = response.json()["job_id"]\nprint(f"Job criado: {job_id}")`,
        code: <><K>import</K> requests{'\n\n'}response = requests.<F>post</F>({'\n'}    <S>"{BASE_URL}/api/v1/audio/generate"</S>,{'\n'}    headers={'{'}<S>"X-API-Key"</S>: <S>"dpk_sua_chave_aqui"</S>, <S>"Content-Type"</S>: <S>"application/json"</S>{'}'},{'\n'}    json={'{'}<S>"text"</S>: <S>"Olá, este é um teste."</S>, <S>"voice_id"</S>: <S>"abc123"</S>, <S>"speed"</S>: <N>1.0</N>{'}'}{'\n'}){'\n'}job_id = response.<F>json</F>()[<S>"job_id"</S>]{'\n'}<F>print</F>(<S>f"Job criado: {'{'}</S><P>job_id</P><S>{'}'}"</S>)</>,
      },
      {
        label: 'JavaScript',
        raw: `const response = await fetch("${BASE_URL}/api/v1/audio/generate", {\n  method: "POST",\n  headers: { "X-API-Key": "dpk_sua_chave_aqui", "Content-Type": "application/json" },\n  body: JSON.stringify({ text: "Olá, este é um teste.", voice_id: "abc123", speed: 1.0 })\n});\nconst { job_id } = await response.json();\nconsole.log("Job criado:", job_id);`,
      },
    ],
    responseExample: {
      raw: '{\n  "success": true,\n  "job_id": "job_a1b2c3d4",\n  "status": "processing",\n  "message": "Áudio adicionado à fila de processamento"\n}',
      code: <>{'{\n  '}<S>"success"</S>: <K>true</K>,{'\n  '}<S>"job_id"</S>: <S>"job_a1b2c3d4"</S>,{'\n  '}<S>"status"</S>: <S>"processing"</S>,{'\n  '}<S>"message"</S>: <S>"Áudio adicionado à fila de processamento"</S>{'\n}'}</>,
    },
  },
  {
    method: 'GET', path: '/api/v1/audio/status/{job_id}',
    description: 'Status do job',
    params: [{ name: 'job_id', type: 'string (path)', required: true, desc: 'ID do job retornado pelo /generate' }],
    codeTabs: [
      {
        label: 'cURL',
        raw: `curl -X GET ${BASE_URL}/api/v1/audio/status/job_a1b2c3d4 \\\n  -H "X-API-Key: dpk_sua_chave_aqui"`,
        code: <><S>{`curl -X GET ${BASE_URL}/api/v1/audio/status/job_a1b2c3d4`}</S>{` \\\n  `}<K>-H</K> <S>"X-API-Key: dpk_sua_chave_aqui"</S></>,
      },
      {
        label: 'Python',
        raw: `import requests\nimport time\n\njob_id = "job_a1b2c3d4"\nwhile True:\n    response = requests.get(\n        f"${BASE_URL}/api/v1/audio/status/{job_id}",\n        headers={"X-API-Key": "dpk_sua_chave_aqui"}\n    )\n    status = response.json()["status"]\n    if status == "completed":\n        print("Áudio pronto!")\n        break\n    elif status == "failed":\n        print("Erro no processamento")\n        break\n    time.sleep(5)`,
        code: <><K>import</K> requests{'\n'}<K>import</K> time{'\n\n'}job_id = <S>"job_a1b2c3d4"</S>{'\n'}<K>while</K> <K>True</K>:{'\n'}    response = requests.<F>get</F>({'\n'}        <S>f"{BASE_URL}/api/v1/audio/status/{'{'}</S><P>job_id</P><S>{'}'}"</S>,{'\n'}        headers={'{'}<S>"X-API-Key"</S>: <S>"dpk_sua_chave_aqui"</S>{'}'}{'\n'}    ){'\n'}    status = response.<F>json</F>()[<S>"status"</S>]{'\n'}    <K>if</K> status == <S>"completed"</S>:{'\n'}        <F>print</F>(<S>"Áudio pronto!"</S>){'\n'}        <K>break</K>{'\n'}    <K>elif</K> status == <S>"failed"</S>:{'\n'}        <F>print</F>(<S>"Erro no processamento"</S>){'\n'}        <K>break</K>{'\n'}    time.<F>sleep</F>(<N>5</N>)</>,
      },
      {
        label: 'JavaScript',
        raw: `const jobId = "job_a1b2c3d4";\nconst poll = async () => {\n  const res = await fetch(\`${BASE_URL}/api/v1/audio/status/\${jobId}\`, {\n    headers: { "X-API-Key": "dpk_sua_chave_aqui" }\n  });\n  const { status } = await res.json();\n  if (status === "completed") return "done";\n  if (status === "failed") throw new Error("Falhou");\n  await new Promise(r => setTimeout(r, 5000));\n  return poll();\n};`,
      },
    ],
    responseExample: {
      raw: '{\n  "success": true,\n  "job_id": "job_a1b2c3d4",\n  "status": "completed"\n}',
      code: <>{'{\n  '}<S>"success"</S>: <K>true</K>,{'\n  '}<S>"job_id"</S>: <S>"job_a1b2c3d4"</S>,{'\n  '}<S>"status"</S>: <S>"completed"</S>{'\n}'}</>,
    },
  },
  {
    method: 'GET', path: '/api/v1/audio/download/{job_id}',
    description: 'Download do áudio',
    params: [{ name: 'job_id', type: 'string (path)', required: true, desc: 'ID do job com status completed' }],
    codeTabs: [
      {
        label: 'cURL',
        raw: `curl -X GET ${BASE_URL}/api/v1/audio/download/job_a1b2c3d4 \\\n  -H "X-API-Key: dpk_sua_chave_aqui"`,
        code: <><S>{`curl -X GET ${BASE_URL}/api/v1/audio/download/job_a1b2c3d4`}</S>{` \\\n  `}<K>-H</K> <S>"X-API-Key: dpk_sua_chave_aqui"</S></>,
      },
      {
        label: 'Python',
        raw: `import requests\n\nresponse = requests.get(\n    f"${BASE_URL}/api/v1/audio/download/{job_id}",\n    headers={"X-API-Key": "dpk_sua_chave_aqui"}\n)\ndata = response.json()\naudio_url = data["audio_url"]\nsrt_url = data["srt_url"]              # legenda.srt (palavras, pausas naturais)\nsrt_palavra_url = data["srt_palavra_url"]  # srtsyncpalavra.srt (total fixo)\nsrt_tempo_url = data["srt_tempo_url"]      # srtsynctempo.srt (tempo aleatorio / VEO)\n\n# Baixar o MP3\naudio = requests.get(audio_url)\nwith open("meu_audio.mp3", "wb") as f:\n    f.write(audio.content)`,
        code: <><K>import</K> requests{'\n\n'}response = requests.<F>get</F>({'\n'}    <S>f"{BASE_URL}/api/v1/audio/download/{'{'}</S><P>job_id</P><S>{'}'}"</S>,{'\n'}    headers={'{'}<S>"X-API-Key"</S>: <S>"dpk_sua_chave_aqui"</S>{'}'}{'\n'}){'\n'}data = response.<F>json</F>(){'\n'}audio_url = data[<S>"audio_url"</S>]{'\n'}srt_url = data[<S>"srt_url"</S>]  <C># legenda.srt (palavras, pausas naturais)</C>{'\n'}srt_palavra_url = data[<S>"srt_palavra_url"</S>]  <C># srtsyncpalavra.srt (total fixo)</C>{'\n'}srt_tempo_url = data[<S>"srt_tempo_url"</S>]  <C># srtsynctempo.srt (tempo aleatorio / VEO)</C>{'\n\n'}<C># Baixar o MP3</C>{'\n'}audio = requests.<F>get</F>(audio_url){'\n'}<K>with</K> <F>open</F>(<S>"meu_audio.mp3"</S>, <S>"wb"</S>) <K>as</K> f:{'\n'}    f.<F>write</F>(audio.content)</>,
      },
      {
        label: 'JavaScript',
        raw: `const response = await fetch(\`${BASE_URL}/api/v1/audio/download/\${jobId}\`, {\n  headers: { "X-API-Key": "dpk_sua_chave_aqui" }\n});\nconst { audio_url, srt_url, srt_palavra_url, srt_tempo_url } = await response.json();\nconsole.log("MP3:", audio_url);\nconsole.log("Legenda:", srt_url);\nconsole.log("Por palavra:", srt_palavra_url);\nconsole.log("Por tempo:", srt_tempo_url);`,
      },
    ],
    responseExample: {
      raw: '{\n  "success": true,\n  "job_id": "job_a1b2c3d4",\n  "audio_url": "https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/audio.mp3",\n  "srt_url": "https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/legenda.srt",\n  "srt_palavra_url": "https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/srtsyncpalavra.srt",\n  "srt_tempo_url": "https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/srtsynctempo.srt",\n  "srt_veo_url": "https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/srtsynctempo.srt"\n}',
      code: <>{'{\n  '}<S>"success"</S>: <K>true</K>,{'\n  '}<S>"job_id"</S>: <S>"job_a1b2c3d4"</S>,{'\n  '}<S>"audio_url"</S>: <S>"https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/audio.mp3"</S>,{'\n  '}<S>"srt_url"</S>: <S>"https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/legenda.srt"</S>,{'\n  '}<S>"srt_palavra_url"</S>: <S>"https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/srtsyncpalavra.srt"</S>,{'\n  '}<S>"srt_tempo_url"</S>: <S>"https://cdn.darkplanner.com.br/audios/job_a1b2c3d4/srtsynctempo.srt"</S>{'\n}'}</>,
    },
  },
  {
    method: 'GET', path: '/api/v1/audio/usage',
    description: 'Uso diário e limites',
    params: [],
    codeTabs: [
      {
        label: 'cURL',
        raw: `curl -X GET ${BASE_URL}/api/v1/audio/usage \\\n  -H "X-API-Key: dpk_sua_chave_aqui"`,
        code: <><S>{`curl -X GET ${BASE_URL}/api/v1/audio/usage`}</S>{` \\\n  `}<K>-H</K> <S>"X-API-Key: dpk_sua_chave_aqui"</S></>,
      },
      {
        label: 'Python',
        raw: `import requests\n\nresponse = requests.get(\n    "${BASE_URL}/api/v1/audio/usage",\n    headers={"X-API-Key": "dpk_sua_chave_aqui"}\n)\nusage = response.json()\nprint(f"Usados hoje: {usage['used_today']}/{usage['limit']}")`,
        code: <><K>import</K> requests{'\n\n'}response = requests.<F>get</F>({'\n'}    <S>"{BASE_URL}/api/v1/audio/usage"</S>,{'\n'}    headers={'{'}<S>"X-API-Key"</S>: <S>"dpk_sua_chave_aqui"</S>{'}'}{'\n'}){'\n'}usage = response.<F>json</F>(){'\n'}<F>print</F>(<S>f"Usados hoje: {'{'}</S><P>usage['used_today']</P><S>{'}'}/<S>{'{'}</S></S><P>usage['limit']</P><S>{'}'}"</S>)</>,
      },
      {
        label: 'JavaScript',
        raw: `const response = await fetch("${BASE_URL}/api/v1/audio/usage", {\n  headers: { "X-API-Key": "dpk_sua_chave_aqui" }\n});\nconst usage = await response.json();\nconsole.log(\`Usados: \${usage.used_today}/\${usage.limit}\`);`,
      },
    ],
    responseExample: {
      raw: '{\n  "used_today": 5,\n  "limit": 60,\n  "remaining": 55,\n  "can_generate": true,\n  "plan_type": "plus",\n  "max_characters_per_audio": 200000\n}',
      code: <>{'{\n  '}<S>"used_today"</S>: <N>5</N>,{'\n  '}<S>"limit"</S>: <N>60</N>,{'\n  '}<S>"remaining"</S>: <N>55</N>,{'\n  '}<S>"can_generate"</S>: <K>true</K>,{'\n  '}<S>"plan_type"</S>: <S>"plus"</S>,{'\n  '}<S>"max_characters_per_audio"</S>: <N>200000</N>{'\n}'}</>,
    },
  },
  {
    method: 'GET', path: '/api/v1/audio/multivoice/info',
    description: 'Info pra usar modo multivoz',
    params: [],
    codeTabs: [
      {
        label: 'cURL',
        raw: `curl -X GET ${BASE_URL}/api/v1/audio/multivoice/info \\\n  -H "X-API-Key: dpk_sua_chave_aqui"`,
        code: <><S>{`curl -X GET ${BASE_URL}/api/v1/audio/multivoice/info`}</S>{` \\\n  `}<K>-H</K> <S>"X-API-Key: dpk_sua_chave_aqui"</S></>,
      },
      {
        label: 'Python',
        raw: `import requests\n\n# 1. Descobrir voice_id da Multivoz\ninfo = requests.get(\n    "${BASE_URL}/api/v1/audio/multivoice/info",\n    headers={"X-API-Key": "dpk_sua_chave_aqui"}\n).json()\n\nmultivoice_voice_id = info["voice_id"]\n\n# 2. Gerar áudio multivoz (use slugs reais de /voices)\nresponse = requests.post(\n    "${BASE_URL}/api/v1/audio/generate",\n    headers={"X-API-Key": "dpk_sua_chave_aqui", "Content-Type": "application/json"},\n    json={\n        "text": "[mark] Olá pessoal [aami] Tudo bem por aí?",\n        "voice_id": multivoice_voice_id,\n        "is_multivoice": True\n    }\n)\nprint(response.json())  # {"job_id": "...", "status": "processing"}`,
        code: <><K>import</K> requests{'\n\n'}<C># 1. Descobrir voice_id da Multivoz</C>{'\n'}info = requests.<F>get</F>({'\n'}    <S>"{BASE_URL}/api/v1/audio/multivoice/info"</S>,{'\n'}    headers={'{'}<S>"X-API-Key"</S>: <S>"dpk_sua_chave_aqui"</S>{'}'}{'\n'}).<F>json</F>(){'\n\n'}multivoice_voice_id = info[<S>"voice_id"</S>]{'\n\n'}<C># 2. Gerar áudio multivoz (use slugs reais de /voices)</C>{'\n'}response = requests.<F>post</F>({'\n'}    <S>"{BASE_URL}/api/v1/audio/generate"</S>,{'\n'}    headers={'{'}<S>"X-API-Key"</S>: <S>"dpk_sua_chave_aqui"</S>, <S>"Content-Type"</S>: <S>"application/json"</S>{'}'},{'\n'}    json={'{'}{'\n'}        <S>"text"</S>: <S>"[mark] Olá pessoal [aami] Tudo bem por aí?"</S>,{'\n'}        <S>"voice_id"</S>: multivoice_voice_id,{'\n'}        <S>"is_multivoice"</S>: <K>True</K>{'\n'}    {'}'}{'\n'})</>,
      },
      {
        label: 'JavaScript',
        raw: `// 1. Descobrir voice_id da Multivoz\nconst info = await fetch("${BASE_URL}/api/v1/audio/multivoice/info", {\n  headers: { "X-API-Key": "dpk_sua_chave_aqui" }\n}).then(r => r.json());\n\nconst multivoiceVoiceId = info.voice_id;\n\n// 2. Gerar áudio multivoz\nconst response = await fetch("${BASE_URL}/api/v1/audio/generate", {\n  method: "POST",\n  headers: { "X-API-Key": "dpk_sua_chave_aqui", "Content-Type": "application/json" },\n  body: JSON.stringify({\n    text: "[mark] Olá pessoal [aami] Tudo bem por aí?",\n    voice_id: multivoiceVoiceId,\n    is_multivoice: true\n  })\n});`,
      },
    ],
    responseExample: {
      raw: '{\n  "voice_id": "cc600bd5-833e-4806-93d9-fb04b3724042",\n  "name": "Multivoz",\n  "slug": "multivoz",\n  "max_narrators": 6,\n  "slug_regex": "[a-zA-Z0-9_]+",\n  "credit_cost": "1 crédito por narrador único (cobrado quando status=completed)",\n  "example": {\n    "voice_id": "cc600bd5-833e-4806-93d9-fb04b3724042",\n    "is_multivoice": true,\n    "text": "[mark] Olá [aami] Tudo bem?"\n  }\n}',
      code: <>{'{\n  '}<S>"voice_id"</S>: <S>"cc600bd5-833e-4806-93d9-fb04b3724042"</S>,{'\n  '}<S>"name"</S>: <S>"Multivoz"</S>,{'\n  '}<S>"slug"</S>: <S>"multivoz"</S>,{'\n  '}<S>"max_narrators"</S>: <N>6</N>,{'\n  '}<S>"slug_regex"</S>: <S>"[a-zA-Z0-9_]+"</S>,{'\n  '}<S>"credit_cost"</S>: <S>"1 crédito por narrador único (cobrado quando status=completed)"</S>,{'\n  '}<S>"example"</S>: {'{'}{'\n    '}<S>"voice_id"</S>: <S>"cc600bd5-833e-4806-93d9-fb04b3724042"</S>,{'\n    '}<S>"is_multivoice"</S>: <K>true</K>,{'\n    '}<S>"text"</S>: <S>"[mark] Olá [aami] Tudo bem?"</S>{'\n  }'}{'\n}'}</>,
    },
  },
  {
    method: 'GET', path: '/api/v1/audio/docs',
    description: 'Documentação JSON',
    params: [],
    codeTabs: [
      {
        label: 'cURL',
        raw: `curl -X GET ${BASE_URL}/api/v1/audio/docs`,
        code: <><S>{`curl -X GET ${BASE_URL}/api/v1/audio/docs`}</S></>,
      },
      {
        label: 'Python',
        raw: `import requests\nresponse = requests.get("${BASE_URL}/api/v1/audio/docs")\nprint(response.json())`,
        code: <><K>import</K> requests{'\n'}response = requests.<F>get</F>(<S>"{BASE_URL}/api/v1/audio/docs"</S>){'\n'}<F>print</F>(response.<F>json</F>())</>,
      },
      {
        label: 'JavaScript',
        raw: `const response = await fetch("${BASE_URL}/api/v1/audio/docs");\nconst docs = await response.json();\nconsole.log(docs);`,
        code: <><K>const</K> response = <K>await</K> <F>fetch</F>(<S>"{BASE_URL}/api/v1/audio/docs"</S>);{'\n'}<K>const</K> docs = <K>await</K> response.<F>json</F>();{'\n'}<F>console.log</F>(docs);</>,
      },
    ],
    responseExample: {
      raw: '{\n  "name": "DarkPlanner Public Audio API",\n  "version": "1.0",\n  "base_url": "/api/v1/audio",\n  "authentication": { ... },\n  "endpoints": [ ... ],\n  "error_codes": { ... }\n}',
      code: <>{'{\n  '}<S>"name"</S>: <S>"DarkPlanner Public Audio API"</S>,{'\n  '}<S>"version"</S>: <S>"1.0"</S>,{'\n  '}<S>"base_url"</S>: <S>"/api/v1/audio"</S>,{'\n  '}<S>"authentication"</S>: {'{ ... }'},{'\n  '}<S>"endpoints"</S>: {'[ ... ]'},  {'\n  '}<S>"error_codes"</S>: {'{ ... }'}{'\n}'}</>,
    },
  },
];

// ═══════════════════════════════════════════════════════════════
// PLAYGROUND COMPONENT
// ═══════════════════════════════════════════════════════════════

const PLAYGROUND_ENDPOINTS = [
  { value: 'voices', label: 'Listar Vozes', method: 'GET', path: '/api/v1/audio/voices' },
  { value: 'generate', label: 'Gerar Áudio', method: 'POST', path: '/api/v1/audio/generate' },
  { value: 'status', label: 'Status do Job', method: 'GET', path: '/api/v1/audio/status/{job_id}' },
  { value: 'download', label: 'Download', method: 'GET', path: '/api/v1/audio/download/{job_id}' },
  { value: 'usage', label: 'Uso Diário', method: 'GET', path: '/api/v1/audio/usage' },
];

function ApiPlayground({ prefilledKey, hasActiveKey, onUsageLoaded }) {
  const navigate = useNavigate();
  const { isMobile } = useSidebar();

  const [apiKey, setApiKey] = useState(prefilledKey || '');
  const [keyVisible, setKeyVisible] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState('voices');
  const [loading, setLoading] = useState(false);
  const [curlPreview, setCurlPreview] = useState('');
  const [responseData, setResponseData] = useState(null);
  const [responseError, setResponseError] = useState(null);

  // Generate fields
  const [text, setText] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [speed, setSpeed] = useState(1.0);
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voicesError, setVoicesError] = useState('');

  // Status/Download fields
  const [jobId, setJobId] = useState('');

  // Update prefilled key
  useEffect(() => { if (prefilledKey) setApiKey(prefilledKey); }, [prefilledKey]);

  // Load voices when key is valid and endpoint is generate
  useEffect(() => {
    if (selectedEndpoint === 'generate' && apiKey && apiKey.startsWith('dpk_')) {
      loadVoices();
    }
    // eslint-disable-next-line
  }, [selectedEndpoint, apiKey]);

  // Fetch usage when key is valid (to update limits table)
  useEffect(() => {
    if (apiKey && apiKey.startsWith('dpk_') && onUsageLoaded) {
      axios.get(`${BACKEND_URL}/api/v1/audio/usage`, {
        headers: { 'X-API-Key': apiKey },
      }).then(res => onUsageLoaded(res.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [apiKey]);

  const loadVoices = async () => {
    setLoadingVoices(true);
    setVoicesError('');
    try {
      const res = await axios.get(`${BACKEND_URL}/api/v1/audio/voices`, {
        headers: { 'X-API-Key': apiKey },
      });
      setVoices(res.data?.voices || []);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao carregar vozes';
      setVoicesError(detail);
      setVoices([]);
    }
    setLoadingVoices(false);
  };

  const buildCurl = useCallback(() => {
    const ep = PLAYGROUND_ENDPOINTS.find(e => e.value === selectedEndpoint);
    if (!ep) return '';

    let url = `${BASE_URL}${ep.path}`;
    let curl = `curl -X ${ep.method} ${url}`;
    curl += ` \\\n  -H "X-API-Key: ${apiKey || 'dpk_sua_chave_aqui'}"`;

    if (selectedEndpoint === 'status') {
      curl = curl.replace('{job_id}', jobId || 'SEU_JOB_ID');
    } else if (selectedEndpoint === 'download') {
      curl = curl.replace('{job_id}', jobId || 'SEU_JOB_ID');
    } else if (selectedEndpoint === 'generate') {
      curl += ` \\\n  -H "Content-Type: application/json"`;
      const body = { text: text || 'Texto de exemplo', voice_id: voiceId || 'voice_id', speed };
      curl += ` \\\n  -d '${JSON.stringify(body)}'`;
    }
    return curl;
  }, [selectedEndpoint, apiKey, jobId, text, voiceId, speed]);

  // Update curl preview
  useEffect(() => {
    setCurlPreview(buildCurl());
  }, [buildCurl]);

  const handleSend = async () => {
    if (!apiKey || !apiKey.startsWith('dpk_')) {
      toast.error('Insira uma API Key válida (formato: dpk_...)');
      return;
    }

    setLoading(true);
    setResponseData(null);
    setResponseError(null);

    try {
      const headers = { 'X-API-Key': apiKey };
      let res;

      switch (selectedEndpoint) {
        case 'voices':
          res = await axios.get(`${BACKEND_URL}/api/v1/audio/voices`, { headers });
          break;
        case 'generate':
          if (!text || text.length < 10) { toast.error('Texto deve ter pelo menos 10 caracteres'); setLoading(false); return; }
          if (!voiceId) { toast.error('Selecione uma voz'); setLoading(false); return; }
          res = await axios.post(`${BACKEND_URL}/api/v1/audio/generate`, { text, voice_id: voiceId, speed }, { headers: { ...headers, 'Content-Type': 'application/json' } });
          break;
        case 'status':
          if (!jobId) { toast.error('Informe o Job ID'); setLoading(false); return; }
          res = await axios.get(`${BACKEND_URL}/api/v1/audio/status/${jobId}`, { headers });
          break;
        case 'download':
          if (!jobId) { toast.error('Informe o Job ID'); setLoading(false); return; }
          res = await axios.get(`${BACKEND_URL}/api/v1/audio/download/${jobId}`, { headers });
          break;
        case 'usage':
          res = await axios.get(`${BACKEND_URL}/api/v1/audio/usage`, { headers });
          break;
        default: break;
      }

      setResponseData(res?.data || null);
    } catch (err) {
      const status = err.response?.status || 0;
      const detail = err.response?.data?.detail || err.message || 'Erro desconhecido';
      setResponseError({ status, detail });
    }
    setLoading(false);
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '8px',
    background: 'var(--dp-bg-input)', border: '1px solid var(--dp-border-subtle)',
    color: 'var(--dp-text-primary)', fontSize: '13px', fontFamily: 'var(--dp-font)',
    outline: 'none',
  };

  const labelStyle = {
    fontSize: '12px', fontWeight: 600, color: 'var(--dp-text-secondary)',
    fontFamily: 'var(--dp-font)', marginBottom: '6px', display: 'block',
  };

  return (
    <div className="dp-top-accent" style={{
      background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
      borderRadius: '14px', padding: '24px', marginTop: '8px',
    }}>
      {/* No API Key banner — hide when field has value or user has active key */}
      {!apiKey && !hasActiveKey && (
        <div style={{
          background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.20)',
          borderRadius: '10px', padding: '12px 16px', marginBottom: '20px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <AlertTriangle size={16} style={{ color: '#FBBF24', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: '#FBBF24', fontFamily: 'var(--dp-font)', flex: 1 }}>
            Você ainda não tem uma API Key. Gere uma na página Meu Plano.
          </span>
          <button onClick={() => navigate('/meu-plano')} style={{
            padding: '6px 14px', borderRadius: '8px',
            background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.30)',
            color: '#FBBF24', fontSize: '12px', fontWeight: 600,
            fontFamily: 'var(--dp-font)', cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            Ir para Meu Plano
          </button>
        </div>
      )}

      {/* API Key input */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>API Key</label>
        <div style={{ position: 'relative' }}>
          <input
            type={keyVisible ? 'text' : 'password'}
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            placeholder="dpk_sua_chave_aqui"
            style={{ ...inputStyle, paddingRight: '40px', fontFamily: 'monospace' }}
          />
          <button onClick={() => setKeyVisible(!keyVisible)} style={{
            position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
          }}>
            {keyVisible ? <EyeOff size={16} style={{ color: 'var(--dp-text-tertiary)' }} /> : <Eye size={16} style={{ color: 'var(--dp-text-tertiary)' }} />}
          </button>
        </div>
      </div>

      {/* Endpoint selector */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Endpoint</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {PLAYGROUND_ENDPOINTS.map(ep => (
            <button
              key={ep.value}
              onClick={() => { setSelectedEndpoint(ep.value); setResponseData(null); setResponseError(null); }}
              style={{
                padding: '8px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                fontFamily: 'var(--dp-font)', cursor: 'pointer',
                background: selectedEndpoint === ep.value ? 'rgba(13, 174, 255, 0.12)' : 'var(--dp-bg-secondary)',
                color: selectedEndpoint === ep.value ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                border: `1px solid ${selectedEndpoint === ep.value ? 'rgba(13, 174, 255, 0.30)' : 'var(--dp-border-subtle)'}`,
                transition: 'all 0.2s ease',
              }}
            >
              <MethodBadge method={ep.method} /> <span style={{ marginLeft: '6px' }}>{ep.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Dynamic fields */}
      {selectedEndpoint === 'generate' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
          <div>
            <label style={labelStyle}>Texto *</label>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Digite o texto para converter em áudio..."
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={labelStyle}>Voz *</label>
              <select
                value={voiceId}
                onChange={e => setVoiceId(e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor: voicesError ? 'rgba(248, 113, 113, 0.5)' : undefined,
                }}
              >
                <option value="">
                  {loadingVoices
                    ? 'Carregando vozes...'
                    : voicesError
                      ? `Erro — ${voicesError}`
                      : voices.length === 0
                        ? 'Preencha a API Key acima'
                        : 'Selecione uma voz'}
                </option>
                {voices.map(v => (
                  <option key={v.id} value={v.id}>{v.name} ({v.gender || '?'})</option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: '160px' }}>
              <label style={labelStyle}>Velocidade: {speed.toFixed(1)}</label>
              <input
                type="range" min="0.7" max="1.2" step="0.1"
                value={speed}
                onChange={e => setSpeed(parseFloat(e.target.value))}
                style={{ width: '100%', marginTop: '8px', accentColor: 'var(--dp-blue-500)' }}
              />
            </div>
          </div>
        </div>
      )}

      {(selectedEndpoint === 'status' || selectedEndpoint === 'download') && (
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Job ID *</label>
          <input
            type="text"
            value={jobId}
            onChange={e => setJobId(e.target.value)}
            placeholder="job_a1b2c3d4"
            style={{ ...inputStyle, fontFamily: 'monospace' }}
          />
        </div>
      )}

      {/* Send button */}
      <button className="dp-btn-cta dp-btn-cta--sm" onClick={handleSend} disabled={loading} style={{ marginBottom: '20px' }}>
        {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={14} />}
        {loading ? 'Enviando...' : 'Enviar Request'}
        <ArrowRight size={14} />
      </button>

      {/* Request / Response panels */}
      <div style={{
        display: 'flex', gap: '12px',
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        {/* Request (cURL) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: '8px',
          }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
              Request (cURL)
            </span>
          </div>
          <div style={{ position: 'relative' }}>
            <CopyButton text={curlPreview} />
            <pre style={{ ...codeBlockStyle, minHeight: '120px', maxHeight: '420px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {curlPreview || '# Selecione um endpoint e clique em Enviar'}
            </pre>
          </div>
        </div>

        {/* Response */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)' }}>
              Response
              {responseData && (
                <span style={{ marginLeft: '8px', color: '#34D399', fontWeight: 600 }}>200 OK</span>
              )}
              {responseError && (
                <span style={{ marginLeft: '8px', color: '#F87171', fontWeight: 600 }}>{responseError.status} Error</span>
              )}
            </span>
          </div>
          <div style={{ position: 'relative' }}>
            {responseData && <CopyButton text={JSON.stringify(responseData, null, 2)} />}
            <pre style={{
              ...codeBlockStyle,
              minHeight: '120px',
              // Respostas grandes (ex.: lista de vozes) rolam dentro do card
              // em vez de esticar a página inteira.
              maxHeight: '420px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: responseError ? '#F87171' : '#E0E0E0',
            }}>
              {loading && '⏳ Aguardando resposta...'}
              {!loading && !responseData && !responseError && '// A resposta aparecerá aqui'}
              {responseData && JSON.stringify(responseData, null, 2)}
              {responseError && JSON.stringify(responseError, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FLOW DIAGRAM
// ═══════════════════════════════════════════════════════════════

function FlowDiagram() {
  const { isMobile } = useSidebar();
  const steps = [
    { label: 'Gerar Key', icon: <Key size={16} />, method: null },
    { label: 'Listar Vozes', icon: <Mic size={16} />, method: 'GET' },
    { label: 'Gerar Áudio', icon: <Zap size={16} />, method: 'POST' },
    { label: 'Verificar Status', icon: <Activity size={16} />, method: 'GET' },
    { label: 'Download', icon: <Download size={16} />, method: 'GET' },
  ];

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: isMobile ? '8px' : '4px', flexWrap: 'wrap',
      padding: '20px 0',
    }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.label}>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
          }}>
            <div style={{
              width: isMobile ? '48px' : '56px', height: isMobile ? '48px' : '56px',
              borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
              color: 'var(--dp-blue-500)',
            }}>
              {s.icon}
            </div>
            <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', textAlign: 'center' }}>
              {s.label}
            </span>
            {s.method && <MethodBadge method={s.method} />}
          </div>
          {i < steps.length - 1 && (
            <ArrowRight size={16} style={{ color: 'var(--dp-text-tertiary)', flexShrink: 0, marginBottom: '24px' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ERROR CODES + LIMITES (tabelas das seções e do Markdown exportado)
// ═══════════════════════════════════════════════════════════════

const ERROR_CODES = [
  { code: '400', color: '#FBBF24', desc: 'Requisição inválida — texto muito curto/longo, parâmetros inválidos' },
  { code: '401', color: '#F87171', desc: 'API key inválida, não encontrada ou revogada' },
  { code: '403', color: '#F87171', desc: 'Plano não autorizado, assinatura inativa ou limite de IPs atingido' },
  { code: '404', color: '#FB923C', desc: 'Job não encontrado' },
  { code: '429', color: '#FB923C', desc: 'Rate limit ou limite diário atingido (verificar header Retry-After)' },
  { code: '500', color: '#F87171', desc: 'Erro interno do servidor' },
];

const DEFAULT_LIMITS_TABLE = [
  { label: 'Áudios por dia', value: 'Conforme seu plano (compartilhado com dashboard)' },
  { label: 'Caracteres por áudio', value: 'Conforme seu plano (por geração, não acumulado)' },
  { label: 'Requests por minuto', value: '10' },
];

// ═══════════════════════════════════════════════════════════════
// DOCS EM MARKDOWN — gerado dos MESMOS dados da página
// ═══════════════════════════════════════════════════════════════

function buildDocsMarkdown() {
  const L = [];
  L.push('# DarkPlanner API — Áudio (v1.0)', '');
  L.push('Gere áudios com vozes naturais e receba 3 legendas SRT + texto, via REST.', '');
  L.push(`Base URL: \`${BASE_URL}/api/v1/audio\``, '');
  L.push('## Autenticação', '');
  L.push('Todas as requisições levam o header `X-API-Key` com a chave no formato `dpk_...`.');
  L.push('A chave é gerada no app (Documentação API > Minha chave). Máximo de 1 chave ativa por conta.', '');
  L.push('## Pausas no texto (SSML)', '');
  L.push('No campo `text` do `POST /generate`, insira pausas com `<break time="Xs"/>` em qualquer ponto.');
  L.push('Aceita decimais (ex.: `<break time="0.5s"/>`, `<break time="1.5s"/>`, `<break time="3s"/>`).', '');
  L.push('## Endpoints', '');
  for (const ep of ENDPOINTS) {
    L.push(`### ${ep.method} ${ep.path}`, '', ep.description, '');
    if (ep.params && ep.params.length) {
      L.push('| Parâmetro | Tipo | Obrigatório | Descrição |', '|---|---|---|---|');
      ep.params.forEach((p) => L.push(`| \`${p.name}\` | ${p.type} | ${p.required ? 'sim' : 'não'} | ${p.desc.replace(/\|/g, '\\|')} |`));
      L.push('');
    }
    const curl = ep.codeTabs && ep.codeTabs.find((tab) => tab.label === 'cURL');
    if (curl && curl.raw) L.push('```bash', curl.raw, '```', '');
    if (ep.responseExample && ep.responseExample.raw) {
      L.push('Resposta:', '```json', ep.responseExample.raw, '```', '');
    }
  }
  L.push('## Limites', '');
  DEFAULT_LIMITS_TABLE.forEach((r) => L.push(`- ${r.label}: ${r.value}`));
  L.push('', '## Códigos de erro', '');
  ERROR_CODES.forEach((r) => L.push(`- ${r.code}: ${r.desc}`));
  L.push('');
  return L.join('\n');
}

export default function ApiDocsPage({ onLogout }) {
  usePageTitle('Documentação API');
  const navigate = useNavigate();
  const { subscriptionData, isLoading, isSuperAdmin } = useAdmin();

  const planType = subscriptionData?.plan_type?.toLowerCase() || '';
  const hasApiAccess = ['pro', 'plus', 'master'].includes(planType) || isSuperAdmin;

  // Prefill API key
  const [prefilledKey, setPrefilledKey] = useState('');
  const [hasActiveKey, setHasActiveKey] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [activeSection, setActiveSection] = useState('inicio');

  // Volta para o app (mantém a origem; se entrou direto, cai no gerador de áudio).
  const backToApp = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/audio-generator');
  }, [navigate]);

  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  }, []);

  // Menu → endpoint: abre o card (catálogo) e rola até ele.
  const [openSignals, setOpenSignals] = useState({});
  const goToEndpoint = useCallback((method, path) => {
    const id = endpointId(method, path);
    setOpenSignals((s) => ({ ...s, [id]: (s[id] || 0) + 1 }));
    setTimeout(() => scrollToSection(id), 30); // deixa o card abrir antes de rolar
  }, [scrollToSection]);

  // Docs em Markdown — copiar (p/ colar em qualquer IA) ou baixar .md.
  const copyDocsMarkdown = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildDocsMarkdown());
      toast.success('Documentação copiada em Markdown. Cole em qualquer IA para integrar.');
    } catch { toast.error('Falha ao copiar'); }
  }, []);
  const downloadDocsMarkdown = useCallback(() => {
    const blob = new Blob([buildDocsMarkdown()], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'darkplanner-api.md';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }, []);

  // Scroll-spy: seções fixas + cada endpoint individualmente.
  useEffect(() => {
    if (!hasApiAccess) return;
    const ids = [
      ...NAV_SECTIONS.flatMap((g) => g.items.map((i) => i.id)),
      ...ENDPOINTS.map((ep) => endpointId(ep.method, ep.path)),
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-24px 0px -70% 0px', threshold: 0 }
    );
    const timer = setTimeout(() => {
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    }, 200); // espera o conteúdo (api key, etc.) montar
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [hasApiAccess]);

  const fetchApiKey = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BACKEND_URL}/api/audio-api/keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const keys = res.data?.keys || [];
      const active = keys.find(k => k.is_active);
      setHasActiveKey(!!active);
      if (active) setPrefilledKey(''); // Don't prefill — user has key but we don't store plaintext
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (hasApiAccess) fetchApiKey();
  }, [hasApiAccess, fetchApiKey]);

  // Build dynamic limits table based on usage data
  const limitsTable = usageData ? [
    {
      label: 'Áudios por dia',
      value: usageData.limit === -1
        ? 'Ilimitado (admin)'
        : `${usageData.limit} (usado hoje: ${usageData.used_today})`,
    },
    {
      label: 'Caracteres por áudio',
      value: usageData.max_characters_per_audio === -1
        ? 'Ilimitado (admin)'
        : Number(usageData.max_characters_per_audio).toLocaleString('pt-BR'),
    },
    { label: 'Requests por minuto', value: '10' },
  ] : DEFAULT_LIMITS_TABLE;

  // Sem acesso: página standalone, centralizada, com botão de voltar.
  if (!isLoading && !hasApiAccess) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0A0A0A', fontFamily: 'var(--dp-font)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '40px 20px', textAlign: 'center',
      }}>
        <AlertTriangle size={44} style={{ color: '#FBBF24', marginBottom: '18px' }} />
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--dp-text-primary)', margin: '0 0 12px' }}>
          Acesso restrito
        </h2>
        <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', margin: '0 0 24px', lineHeight: 1.6, maxWidth: '420px' }}>
          A documentação da API está disponível apenas nos planos Pro e Plus.
        </p>
        <button onClick={backToApp} className="dp-btn-secondary" style={{
          display: 'inline-flex', alignItems: 'center', gap: '8px',
          padding: '10px 18px', borderRadius: '12px', cursor: 'pointer',
          background: 'transparent', border: '1px solid var(--dp-border-medium)',
          color: 'var(--dp-text-secondary)', fontSize: '13px', fontWeight: 600, fontFamily: 'var(--dp-font)',
        }}>
          <ArrowLeft size={15} /> Voltar para o app
        </button>
      </div>
    );
  }

  // Item simples do menu lateral
  const navItem = (id, label) => (
    <button
      key={id}
      className={`dp-side-item${activeSection === id ? ' active' : ''}`}
      onClick={() => scrollToSection(id)}
    >
      {label}
    </button>
  );
  const navLabel = (text) => (
    <div style={{
      fontSize: '10px', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase',
      color: 'var(--dp-text-tertiary)', margin: '18px 12px 8px',
    }}>
      {text}
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', fontFamily: 'var(--dp-font)' }}>
      <style>{`
        .dp-docs-side { position:fixed; left:0; top:0; bottom:0; width:256px; z-index:50; display:flex; flex-direction:column; background:#0D0D0D; border-right:1px solid var(--dp-border-subtle); }
        .dp-docs-content { margin-left:256px; }
        .dp-docs-mobilebar { display:none; }
        .dp-side-item { display:block; width:100%; text-align:left; padding:7px 12px; margin-bottom:2px; border-radius:8px; border:none; background:transparent; color:var(--dp-text-secondary); font-size:13px; font-weight:500; font-family:var(--dp-font); cursor:pointer; transition:color .15s, background .15s; }
        .dp-side-item:hover { color:var(--dp-text-primary); background:rgba(255,255,255,0.04); }
        .dp-side-item.active { background:rgba(13,174,255,0.10); color:var(--dp-blue-500); font-weight:600; }
        .dp-side-ep { display:flex; align-items:center; gap:8px; width:100%; text-align:left; padding:6px 12px; margin-bottom:2px; border-radius:8px; border:none; background:transparent; cursor:pointer; transition:background .15s; }
        .dp-side-ep:hover { background:rgba(255,255,255,0.04); }
        .dp-side-ep.active { background:rgba(13,174,255,0.10); }
        .dp-side-ep .ep-method { font-size:9px; font-weight:800; letter-spacing:.5px; padding:2px 6px; border-radius:5px; font-family:monospace; flex-shrink:0; }
        .dp-side-ep .ep-method.get { color:#34D399; background:rgba(52,211,153,0.12); border:1px solid rgba(52,211,153,0.25); }
        .dp-side-ep .ep-method.post { color:#60A5FA; background:rgba(96,165,250,0.12); border:1px solid rgba(96,165,250,0.25); }
        .dp-side-ep .ep-path { font-size:11.5px; font-family:monospace; color:var(--dp-text-secondary); word-break:break-all; line-height:1.35; transition:color .15s; }
        .dp-side-ep:hover .ep-path, .dp-side-ep.active .ep-path { color:var(--dp-text-primary); }
        .dp-side-ep.active .ep-path { color:var(--dp-blue-500); }
        .dp-docs-back { display:inline-flex; align-items:center; justify-content:center; gap:7px; width:100%; padding:9px 14px; border-radius:10px; cursor:pointer; background:transparent; border:1px solid var(--dp-border-medium); color:var(--dp-text-secondary); font-size:12.5px; font-weight:600; font-family:var(--dp-font); transition:border-color .2s, color .2s, background .2s; white-space:nowrap; }
        .dp-docs-back:hover { border-color:var(--dp-blue-500); color:var(--dp-text-primary); background:var(--dp-bg-card); }
        .dp-docs-back svg { transition: transform .2s; }
        .dp-docs-back:hover svg { transform: translateX(-2px); }
        .dp-docs-mdbtn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border-radius:10px; cursor:pointer; background:transparent; border:1px solid var(--dp-border-medium); color:var(--dp-text-secondary); font-size:12.5px; font-weight:600; font-family:var(--dp-font); transition:border-color .2s, color .2s, background .2s, transform .2s; white-space:nowrap; }
        .dp-docs-mdbtn:hover { border-color:var(--dp-blue-500); color:var(--dp-text-primary); background:var(--dp-bg-card); transform:translateY(-1px); }
        @media (max-width: 1023px) {
          .dp-docs-side { display:none; }
          .dp-docs-content { margin-left:0; padding-top:56px; }
          .dp-docs-mobilebar { display:flex; position:fixed; top:0; left:0; right:0; height:52px; z-index:50; align-items:center; justify-content:space-between; padding:0 16px; background:rgba(10,10,10,0.9); backdrop-filter:blur(12px); border-bottom:1px solid var(--dp-border-subtle); }
          .dp-docs-mobilebar .dp-docs-back { width:auto; }
        }
      `}</style>

      {/* ─── SIDEBAR de documentação (logo + menu + voltar no rodapé) ─── */}
      <aside className="dp-docs-side">
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '18px 16px', borderBottom: '1px solid var(--dp-border-subtle)', flexShrink: 0,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
            boxShadow: '0 4px 14px rgba(13,174,255,0.28)',
          }}>
            <BookOpen size={17} color="#fff" strokeWidth={2.2} />
          </div>
          <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--dp-text-primary)', letterSpacing: '-0.3px' }}>
            DarkPlanner <span style={{ color: 'var(--dp-blue-500)' }}>API</span>
          </span>
          <span style={{
            fontSize: '9.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px',
            color: 'var(--dp-blue-500)', background: 'rgba(13,174,255,0.08)', border: '1px solid rgba(13,174,255,0.22)',
            letterSpacing: '0.5px', flexShrink: 0, marginLeft: 'auto',
          }}>
            v1.0
          </span>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', padding: '4px 10px 14px' }}>
          {navLabel(NAV_SECTIONS[0].label)}
          {NAV_SECTIONS[0].items.map(({ id, label }) => navItem(id, label))}

          {navLabel('Endpoints de áudio')}
          {ENDPOINTS.map((ep) => {
            const id = endpointId(ep.method, ep.path);
            return (
              <button
                key={id}
                className={`dp-side-ep${activeSection === id ? ' active' : ''}`}
                onClick={() => goToEndpoint(ep.method, ep.path)}
                title={ep.description}
              >
                <span className={`ep-method ${ep.method.toLowerCase()}`}>{ep.method}</span>
                <span className="ep-path">{ep.path.replace('/api/v1/audio', '') || '/'}</span>
              </button>
            );
          })}

          {navLabel(NAV_SECTIONS[1].label)}
          {NAV_SECTIONS[1].items.map(({ id, label }) => navItem(id, label))}
        </nav>

        <div style={{ padding: '14px 14px 16px', borderTop: '1px solid var(--dp-border-subtle)', flexShrink: 0 }}>
          <button className="dp-docs-back" onClick={backToApp}>
            <ArrowLeft size={15} /> Voltar para o app
          </button>
        </div>
      </aside>

      {/* ─── Topbar mobile (sidebar escondida) ─── */}
      <header className="dp-docs-mobilebar">
        <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)' }}>
          DarkPlanner <span style={{ color: 'var(--dp-blue-500)' }}>API</span>
        </span>
        <button className="dp-docs-back" onClick={backToApp}>
          <ArrowLeft size={14} /> Voltar
        </button>
      </header>

      {/* ─── Conteúdo ─── */}
      <div className="dp-docs-content">
        <main style={{ maxWidth: '880px', margin: '0 auto', minWidth: 0, padding: '36px clamp(20px, 4vw, 48px) 64px' }}>

            {/* ═══ HERO (centralizado, padrão de docs de mercado) ═══ */}
            <div id="inicio" style={{
              textAlign: 'center', padding: '28px 0 8px', scrollMarginTop: '24px',
              animation: 'dp-fadeInUp 0.45s ease',
            }}>
              <span style={{
                display: 'inline-block', fontSize: '10.5px', fontWeight: 700, padding: '3px 11px',
                borderRadius: '9999px', color: 'var(--dp-text-secondary)',
                background: 'rgba(255,255,255,0.04)', border: '1px solid var(--dp-border-medium)',
                letterSpacing: '0.5px', marginBottom: '16px',
              }}>
                v1.0
              </span>
              <h1 style={{
                fontSize: '34px', fontWeight: 700, color: 'var(--dp-text-primary)',
                fontFamily: 'var(--dp-font)', margin: '0 0 14px', letterSpacing: '-1px',
              }}>
                DarkPlanner API
              </h1>
              <p style={{
                fontSize: '15px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)',
                margin: '0 auto 22px', lineHeight: 1.7, maxWidth: '600px',
              }}>
                Acesso programático ao gerador de áudio do DarkPlanner. Gere locuções com vozes
                naturais e receba as 3 legendas SRT e o texto, tudo por uma API REST simples.
              </p>

              {/* Base URL — pill central copiável */}
              <button
                onClick={() => { navigator.clipboard.writeText(`${BASE_URL}/api/v1/audio`); toast.success('Base URL copiada!'); }}
                title="Clique para copiar"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  background: 'rgba(13,174,255,0.05)', border: '1px solid rgba(13,174,255,0.25)',
                  borderRadius: '10px', padding: '11px 20px', cursor: 'pointer',
                  marginBottom: '18px', transition: 'border-color .2s, background .2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; e.currentTarget.style.background = 'rgba(13,174,255,0.09)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.25)'; e.currentTarget.style.background = 'rgba(13,174,255,0.05)'; }}
              >
                <code style={{ fontSize: '13.5px', color: 'var(--dp-blue-500)', fontFamily: 'monospace' }}>
                  {BASE_URL}/api/v1/audio
                </code>
                <Copy size={13} style={{ color: 'var(--dp-text-tertiary)' }} />
              </button>

              {/* Docs p/ IAs: copiar Markdown / baixar .md */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '26px' }}>
                <button className="dp-docs-mdbtn" onClick={copyDocsMarkdown}>
                  <Copy size={14} /> Copiar como Markdown
                </button>
                <button className="dp-docs-mdbtn" onClick={downloadDocsMarkdown}>
                  <Download size={14} /> Baixar darkplanner-api.md
                </button>
              </div>

              {/* Aviso de plano (padrão das docs de mercado) */}
              <div style={{
                textAlign: 'left', maxWidth: '640px', margin: '0 auto',
                background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.22)',
                borderRadius: '12px', padding: '14px 18px',
                fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', lineHeight: 1.65,
              }}>
                <strong style={{ color: 'var(--dp-text-primary)' }}>Assinatura ativa obrigatória.</strong>{' '}
                A API de áudio está disponível nos planos Pro e Plus, com os mesmos limites diários do dashboard.{' '}
                <span
                  onClick={() => navigate('/meu-plano')}
                  style={{ color: 'var(--dp-blue-500)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }}
                >
                  Gerencie sua assinatura
                </span>.
              </div>
            </div>

            <div style={{ height: '1px', background: 'var(--dp-border-subtle)', margin: '34px 0 8px' }} />

            {/* ═══ MINHA CHAVE (gerar/exibir/revogar) — movida do Meu Plano ═══ */}
            <AudioApiKeySection onKeysChanged={fetchApiKey} />

            {/* ═══ FLOW DIAGRAM ═══ */}
            <SectionTitle
              id="fluxo"
              icon={<Activity size={16} style={{ color: 'var(--dp-blue-500)' }} />}
              title="Fluxo Completo"
            />
            <div className="dp-top-accent" style={{
              background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
              borderRadius: '14px', padding: '16px',
            }}>
              <FlowDiagram />
            </div>

            {/* ═══ QUICKSTART (3 passos + teste agora) ═══ */}
            <SectionTitle
              id="quick-start"
              icon={<Zap size={16} style={{ color: 'var(--dp-blue-500)' }} />}
              title="Quickstart"
            />
            <p style={{ fontSize: '13.5px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', margin: '-6px 0 18px', lineHeight: 1.6 }}>
              Seu primeiro áudio em menos de 60 segundos.
            </p>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '12px', marginBottom: '22px',
            }}>
              {[
                { n: 1, title: 'Gere sua chave', desc: 'Use a seção Minha chave aqui na documentação e clique em Gerar Chave de API.', go: () => scrollToSection('minha-chave') },
                { n: 2, title: 'Envie um job', desc: 'Faça um POST em /generate com seu texto e o voice_id (liste as vozes em /voices).', go: () => goToEndpoint('POST', '/api/v1/audio/generate') },
                { n: 3, title: 'Baixe seu áudio', desc: 'Consulte /status/:job_id até concluir e pegue as URLs (MP3 + 3 SRTs) em /download/:job_id.', go: () => goToEndpoint('GET', '/api/v1/audio/download/{job_id}') },
              ].map((s) => (
                <button key={s.n} onClick={s.go} style={{
                  textAlign: 'left', background: 'var(--dp-bg-card)',
                  border: '1px solid var(--dp-border-subtle)', borderRadius: '14px',
                  padding: '18px', cursor: 'pointer',
                  transition: 'border-color .2s, transform .2s',
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(13,174,255,0.25)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--dp-border-subtle)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '24px', height: '24px', borderRadius: '50%', marginBottom: '12px',
                    background: 'rgba(13,174,255,0.12)', border: '1px solid rgba(13,174,255,0.30)',
                    color: 'var(--dp-blue-500)', fontSize: '12px', fontWeight: 700, fontFamily: 'var(--dp-font)',
                  }}>
                    {s.n}
                  </span>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--dp-text-primary)', fontFamily: 'var(--dp-font)', marginBottom: '6px' }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: '12.5px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', lineHeight: 1.6 }}>
                    {s.desc}
                  </div>
                </button>
              ))}
            </div>

            <div style={{
              fontSize: '10px', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase',
              color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)', marginBottom: '10px',
            }}>
              Teste agora
            </div>
            <div style={{ position: 'relative' }}>
              <CopyButton text={QUICKSTART_CURL} />
              <pre style={codeBlockStyle}>{QUICKSTART_CURL}</pre>
            </div>

            {/* ═══ AUTHENTICATION ═══ */}
            <SectionTitle
              id="autenticacao"
              icon={<Key size={16} style={{ color: 'var(--dp-blue-500)' }} />}
              title="Autenticação"
            />
            <div style={{
              background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
              borderRadius: '14px', padding: '20px',
            }}>
              <p style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', margin: '0 0 12px 0', lineHeight: 1.7 }}>
                Todas as requisições devem incluir o header <code style={{ color: '#FFCB6B', background: '#0a0a0a', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>X-API-Key</code> com
                sua chave de acesso no formato <code style={{ color: '#C3E88D', background: '#0a0a0a', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>dpk_...</code>
              </p>
              <div style={{ position: 'relative' }}>
                <CopyButton text={`curl -H "X-API-Key: dpk_sua_chave_aqui" ${BASE_URL}/api/v1/audio/voices`} />
                <pre style={codeBlockStyle}>
                  <K>curl</K> <K>-H</K> <S>"X-API-Key: dpk_sua_chave_aqui"</S> {BASE_URL}/api/v1/audio/voices
                </pre>
              </div>
            </div>

            {/* ═══ ENDPOINTS ═══ */}
            <SectionTitle
              id="endpoints"
              icon={<BookOpen size={16} style={{ color: 'var(--dp-blue-500)' }} />}
              title="Endpoints"
            />
            <p style={{ fontSize: '13px', color: 'var(--dp-text-secondary)', fontFamily: 'var(--dp-font)', margin: '-6px 0 16px', lineHeight: 1.6 }}>
              Clique num endpoint para expandir parâmetros, exemplos e resposta. As pausas SSML
              (<code style={{ color: '#FFCB6B', fontSize: '11.5px' }}>{'<break time="Xs"/>'}</code>) estão documentadas dentro do <code style={{ color: '#C3E88D', fontSize: '11.5px' }}>POST /generate</code>.
            </p>
            {ENDPOINTS.map(ep => (
              <EndpointCard key={ep.path} {...ep} openSignal={openSignals[endpointId(ep.method, ep.path)]} />
            ))}

            {/* ═══ PLAYGROUND ═══ */}
            <SectionTitle
              id="playground"
              icon={<Play size={16} style={{ color: 'var(--dp-blue-500)' }} />}
              title="Testar API"
            />
            <ApiPlayground prefilledKey={prefilledKey} hasActiveKey={hasActiveKey} onUsageLoaded={setUsageData} />

            {/* ═══ LIMITS ═══ */}
            <SectionTitle
              id="limites"
              icon={<Activity size={16} style={{ color: 'var(--dp-blue-500)' }} />}
              title="Limites"
            />
            <div style={{
              background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
              borderRadius: '14px', overflow: 'hidden',
            }}>
              <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse', fontFamily: 'var(--dp-font)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--dp-border-subtle)' }}>
                    <th style={{ padding: '14px 20px', textAlign: 'left', color: 'var(--dp-text-tertiary)', fontWeight: 600, fontSize: '12px' }}>Limite</th>
                    <th style={{ padding: '14px 20px', textAlign: 'left', color: 'var(--dp-text-tertiary)', fontWeight: 600, fontSize: '12px' }}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {limitsTable.map((row, i) => (
                    <tr key={row.label} style={{ borderBottom: i < limitsTable.length - 1 ? '1px solid var(--dp-border-subtle)' : 'none' }}>
                      <td style={{ padding: '12px 20px', color: 'var(--dp-text-secondary)' }}>{row.label}</td>
                      <td style={{ padding: '12px 20px', color: 'var(--dp-text-primary)', fontWeight: 600 }}>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ═══ ERROR CODES ═══ */}
            <SectionTitle
              id="erros"
              icon={<AlertTriangle size={16} style={{ color: 'var(--dp-blue-500)' }} />}
              title="Códigos de Erro"
            />
            <div style={{
              background: 'var(--dp-bg-card)', border: '1px solid var(--dp-border-subtle)',
              borderRadius: '14px', overflow: 'hidden',
            }}>
              <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse', fontFamily: 'var(--dp-font)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--dp-border-subtle)' }}>
                    <th style={{ padding: '14px 20px', textAlign: 'left', color: 'var(--dp-text-tertiary)', fontWeight: 600, fontSize: '12px', width: '80px' }}>Código</th>
                    <th style={{ padding: '14px 20px', textAlign: 'left', color: 'var(--dp-text-tertiary)', fontWeight: 600, fontSize: '12px' }}>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  {ERROR_CODES.map((row, i) => (
                    <tr key={row.code} style={{ borderBottom: i < ERROR_CODES.length - 1 ? '1px solid var(--dp-border-subtle)' : 'none' }}>
                      <td style={{ padding: '12px 20px' }}>
                        <span style={{
                          fontSize: '12px', fontWeight: 700, fontFamily: 'monospace',
                          padding: '3px 8px', borderRadius: '6px',
                          color: row.color, background: `${row.color}15`, border: `1px solid ${row.color}30`,
                        }}>
                          {row.code}
                        </span>
                      </td>
                      <td style={{ padding: '12px 20px', color: 'var(--dp-text-secondary)' }}>{row.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bottom spacer */}
            <div style={{ height: '20px' }} />
        </main>
      </div>
    </div>
  );
}
